import { Fragment, useEffect, useState, useRef } from "react";
import HeaderSection from "../header/header.component";
import { Col, Form, Row } from "react-bootstrap";
import {
  netural400,
  successGreen,
  success50,
  success700,
  errorRed,
  error50,
  error900,
  primaryOrange,
  netural50,
  neutral200,
  neturalBlack,
} from "../../constants/color";
import CustomLabel from "../shared/label/custom-label.component";
import CustomButton from "../button/button.component";
import CollapsibleCard from "../collapsible-card/collapsible-card";
import {
  FETCH_BUSINESS_VERIFICATION_INFO,
  HANDLE_BUSINESS_VERIFICATION_REQUEST,
  GET_BUSINESS_VERIFICATION_STATUS,
} from "../../services/graphql/businessAccount";
import { useLazyQuery, useMutation } from "@apollo/client";
import NoticeBanner from "../notice-banner/notice-banner";
import {
  business_approved,
  business_pending,
  business_processing,
  business_rejected,
} from "../../constants/constant";
import LoadingSpinner from "../shared/loading-spinner/loading-spinner.component";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const BusinessInfoForm = () => {
  const [validated, setValidated] = useState(false);
  const [errorFields, setErrorFields] = useState({});
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [reverificationInfo, setReverificationInfo] = useState({
    legalBusinessName: "",
    businessNumber: "",
    taxNumber: "",
    taxRate: "",
    location: "Florida - US",
    termsAndConditions: "",
    privacyPolicy: "",
  });
  const [rejectionReason, setRejectionReason] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const businesprofile = useSelector((state) => state.businesprofile);

  // Stripe verification state
  const [stripeVerificationStatus, setStripeVerificationStatus] =
    useState(null);
  const [isStripeStatusLoading, setIsStripeStatusLoading] = useState(true);
  const [stripeStatusError, setStripeStatusError] = useState(null);
  const [stripeOnboardingUrl, setStripeOnboardingUrl] = useState("");
  const [stripeDashboardUrl, setStripeDashboardUrl] = useState("");
  const [hasStripeErrors, setHasStripeErrors] = useState(false);
  const [stripeErrorMessages, setStripeErrorMessages] = useState([]);

  // Section expansion states
  const [isBusinessInfoExpanded, setIsBusinessInfoExpanded] = useState(true);
  const [isPaymentSetupExpanded, setIsPaymentSetupExpanded] = useState(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "region") {
      setReverificationInfo({
        ...reverificationInfo,
        location: value,
      });
    } else {
      // For taxRate, ensure it is numeric and <= 100
      if (name === "taxRate") {
        if (!/^\d*\.?\d*$/.test(value) || parseFloat(value) > 100) return;

        setReverificationInfo({
          ...reverificationInfo,
          [name]: value === "" ? "" : parseFloat(value) || 0, // Handle empty string & allow 0
        });
      } else {
        setReverificationInfo({
          ...reverificationInfo,
          [name]: value,
        });
      }

      setErrorFields({
        ...errorFields,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;
    let errors = {};

    if (
      reverificationInfo.taxRate === "" || // Prevent empty string
      reverificationInfo.taxRate === null || // Prevent null
      reverificationInfo.taxRate === undefined || // Prevent undefined
      isNaN(reverificationInfo.taxRate) || // Prevent NaN
      reverificationInfo.taxRate < 0 ||
      reverificationInfo.taxRate > 100
    ) {
      errors.taxRate = "Tax rate must be a number between 0 and 100.";
      hasError = true;
    }
    if (
      !reverificationInfo.termsAndConditions ||
      reverificationInfo.termsAndConditions.trim().length < 100
    ) {
      errors.termsAndConditions =
        "Terms and Conditions must be at least 100 characters.";
      hasError = true;
    }
    if (
      !reverificationInfo.privacyPolicy ||
      reverificationInfo.privacyPolicy.trim().length < 100
    ) {
      errors.privacyPolicy = "Privacy Policy must be at least 100 characters.";
      hasError = true;
    }

    setErrorFields(errors);

    if (!hasError) {
      setValidated(true);
      // Set verification type based on current status
      let verificationType = "Pending";
      if (
        verificationStatus === "Approved" ||
        verificationStatus === "Rejected"
      ) {
        verificationType = "Reverifying"; // Re-submission after approval/rejection
      } else if (
        verificationStatus === "Processing" ||
        verificationStatus === "Reverifying"
      ) {
        verificationType = verificationStatus;
      }

      await handleBusinessVerificationRequest({
        variables: {
          verificationInfo: reverificationInfo,
          verificationType: verificationType,
        },
      });
    }
  };

  useEffect(() => {
    getBusinessVerificationInfo(businesprofile?._id);
    handleFetchStripeStatus();
  }, []);

  const getBusinessVerificationInfo = async (id) => {
    // console.log("Fetching business verification info for ID:", id);
    await fetchBusinessVerification({
      variables: {
        business_id: id,
      },
    });
  };

  const [fetchBusinessVerification, { loading: loadingBusinessVerification }] =
    useLazyQuery(FETCH_BUSINESS_VERIFICATION_INFO, {
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (data && data.fetchBusinessVerification) {
          let verificationInfo = data.fetchBusinessVerification;
          // console.log("All value----------------", verificationInfo);
          setVerificationStatus(verificationInfo.verification);
          setRejectionReason(verificationInfo.rejectionReason);
          setReverificationInfo({
            legalBusinessName: verificationInfo.legalBusinessName
              ? verificationInfo.legalBusinessName
              : "",
            businessNumber: verificationInfo.businessNumber
              ? verificationInfo.businessNumber
              : "",
            taxNumber: verificationInfo.taxNumber
              ? verificationInfo.taxNumber
              : "",
            taxRate: verificationInfo.taxRate
              ? parseFloat(verificationInfo.taxRate)
              : "",
            location: verificationInfo.location
              ? verificationInfo.location
              : "",
            termsAndConditions: verificationInfo.termsAndConditions
              ? verificationInfo.termsAndConditions
              : "",
            privacyPolicy: verificationInfo.privacyPolicy
              ? verificationInfo.privacyPolicy
              : "",
          });
          setIsFormDisabled(
            verificationInfo.verification === "Processing" ||
              verificationInfo.verification === "Reverifying"
              ? true
              : false,
          );
        }
      },
    });

  const [
    handleBusinessVerificationRequest,
    { loading: loadingBusinessVerificationRequest },
  ] = useMutation(HANDLE_BUSINESS_VERIFICATION_REQUEST, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data && data.handleBusinessVerificationRequest) {
        const business_response = data.handleBusinessVerificationRequest;
        if (business_response) {
          toast.success(business_response.message, {
            style: { width: "450px" },
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored ",
          });
          getBusinessVerificationInfo(businesprofile?._id);
        } else {
          toast.error(business_response.message, {
            style: { width: "450px" },
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored ",
          });
        }
      }
    },
  });

  // Stripe verification status query
  const [fetchStripeStatus] = useLazyQuery(GET_BUSINESS_VERIFICATION_STATUS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.getBusinessVerificationStatus) {
        const statusData = data.getBusinessVerificationStatus;
        console.log("Fetched Stripe status data:", statusData);
        setStripeVerificationStatus({
          status: statusData.status,
          canAcceptPayments: statusData.canAcceptPayments,
          chargesEnabled: statusData.chargesEnabled,
          payoutsEnabled: statusData.payoutsEnabled,
          detailsSubmitted: statusData.detailsSubmitted,
          message: statusData.message,
          actionRequired: statusData.actionRequired,
        });
        setStripeOnboardingUrl(statusData.onboardingUrl || "");
        setStripeDashboardUrl(statusData.dashboardUrl || "");
        setHasStripeErrors(statusData.hasStripeErrors || false);
        setStripeErrorMessages(statusData.stripeErrorMessages || []);
        setStripeStatusError(null);
        setIsStripeStatusLoading(false);
      }
    },
    onError: (error) => {
      console.error("Error fetching Stripe status:", error);
      setStripeStatusError(
        "Unable to load payment verification status. Please try again.",
      );
      setIsStripeStatusLoading(false);
    },
  });

  const handleFetchStripeStatus = () => {
    setIsStripeStatusLoading(true);
    setStripeStatusError(null);
    console.log("Fetching Stripe status for business ID:", businesprofile?._id);
    fetchStripeStatus({
      variables: {
        businessId: businesprofile?._id, // Replace with actual business ID from context/props
        platform: "portal",
      },
    });
  };

  const handleOpenStripeOnboarding = () => {
    if (stripeOnboardingUrl) {
      window.open(stripeOnboardingUrl, "_blank");
      // Refresh status after 2 seconds to catch any changes
      setTimeout(() => {
        handleFetchStripeStatus();
      }, 2000);
      // Also set up a polling mechanism to check status periodically while modal is open
      const pollInterval = setInterval(() => {
        handleFetchStripeStatus();
      }, 3000); // Check every 3 seconds

      // Clean up polling when user returns
      window.addEventListener(
        "focus",
        () => {
          clearInterval(pollInterval);
          handleFetchStripeStatus(); // Final check when returning to tab
        },
        { once: true },
      );
    }
  };

  const handleOpenStripeDashboard = () => {
    if (stripeDashboardUrl) {
      window.open(stripeDashboardUrl, "_blank");
      // Refresh status when user returns from dashboard
      const pollInterval = setInterval(() => {
        handleFetchStripeStatus();
      }, 2000);

      window.addEventListener(
        "focus",
        () => {
          clearInterval(pollInterval);
          handleFetchStripeStatus();
        },
        { once: true },
      );
    }
  };

  // Helper function to get status colors
  const getStatusColors = (status) => {
    const statusColors = {
      Approved: {
        background: success50,
        border: successGreen,
        icon: success700,
      },
      Rejected: {
        background: error50,
        border: errorRed,
        icon: error900,
      },
      Processing: {
        background: "#FFF3E0",
        border: primaryOrange,
        icon: primaryOrange,
      },
      Reverifying: {
        background: "#FFF3E0",
        border: primaryOrange,
        icon: primaryOrange,
      },
      Pending: {
        background: netural50,
        border: netural400,
        icon: netural400,
      },
    };
    return statusColors[status] || statusColors.Pending;
  };

  // Helper function to determine payment setup status
  const getPaymentSetupStatus = () => {
    if (!stripeVerificationStatus) return "not_started";

    // If there are Stripe errors, prioritize showing that
    if (hasStripeErrors) {
      return stripeVerificationStatus?.canAcceptPayments
        ? "active_with_issues" // Can accept payments but has problems to fix
        : "blocked_with_issues"; // Cannot accept payments and has problems
    }

    // No errors - check normal flow
    if (stripeVerificationStatus?.canAcceptPayments) {
      return "completed"; // Fully verified, no issues
    }

    if (stripeVerificationStatus?.detailsSubmitted) {
      return "in_progress"; // Submitted, awaiting verification
    }

    return "not_started"; // Haven't started
  };

  const paymentSetupStatus = getPaymentSetupStatus();

  // Helper function to get payment setup styling based on status
  const getPaymentSetupStyling = () => {
    const styles = {
      completed: {
        background: success50,
        border: successGreen,
        icon: success700,
        headerIcon: <span style={{ fontSize: "28px" }}>✓</span>,
        subtitle: "Completed - You can manage in Stripe dashboard",
      },
      active_with_issues: {
        background: "#FFF3E0",
        border: primaryOrange,
        icon: primaryOrange,
        headerIcon: <span style={{ fontSize: "28px" }}>⚠</span>,
        subtitle: "Issues Detected - Action required in Stripe",
      },
      blocked_with_issues: {
        background: error50,
        border: errorRed,
        icon: error900,
        headerIcon: <span style={{ fontSize: "28px" }}>✕</span>,
        subtitle: "Verification Issues - Please resolve in Stripe",
      },
      in_progress: {
        background: "#FFF3E0",
        border: primaryOrange,
        icon: primaryOrange,
        headerIcon: <span style={{ fontSize: "28px" }}>⏳</span>,
        subtitle: "In Progress - Stripe is reviewing your account",
      },
      not_started: {
        background: netural50,
        border: netural400,
        icon: netural400,
        headerIcon: <span style={{ fontSize: "28px" }}>💳</span>,
        subtitle: "Not Started - Connect your Stripe account",
      },
    };
    return styles[paymentSetupStatus] || styles.not_started;
  };

  const paymentSetupStyling = getPaymentSetupStyling();

  // Check if both verifications are complete (no errors)
  const isFullyVerified =
    verificationStatus === "Approved" &&
    stripeVerificationStatus?.canAcceptPayments &&
    !hasStripeErrors;

  // Refs for scrolling
  const businessInfoRef = useRef(null);
  const paymentSetupRef = useRef(null);

  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      className="main-div-right-section"
      style={{ backgroundColor: "white" }}
    >
      <Fragment>
        <HeaderSection currentTab={"businessInfo"} />
        {loadingBusinessVerification ||
        loadingBusinessVerificationRequest ||
        isStripeStatusLoading ? (
          <div style={{ flex: 1, height: "90vh", display: "flex", justifyContent: "center", }} >
            <LoadingSpinner size="medium" overlay={false} text="Loading verification status..." />
          </div>
        ) : (
          <div className="second-section-style">
            {/* Overall Status Summary - Banner Above Steps */}
            <NoticeBanner
              variant={
                isFullyVerified
                  ? "success"
                  : hasStripeErrors
                    ? "warning"
                    : "info"
              }
              title={
                isFullyVerified
                  ? "Verification Complete"
                  : hasStripeErrors
                    ? "Action Required - Stripe has verification issues"
                    : verificationStatus === "Approved" &&
                        stripeVerificationStatus?.detailsSubmitted
                      ? "Almost there! Complete Stripe verification"
                      : "Complete both steps to start accepting payments"
              }
              showIcon={true}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginTop: "8px",
                }}
              >
                <div
                  onClick={() => scrollToSection(businessInfoRef)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${
                        verificationStatus === "Approved"
                          ? successGreen
                          : netural400
                      }`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        verificationStatus === "Approved"
                          ? successGreen
                          : "transparent",
                    }}
                  >
                    {verificationStatus === "Approved" && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      color:
                        verificationStatus === "Approved"
                          ? success700
                          : netural400,
                      fontWeight: verificationStatus === "Approved" ? 600 : 500,
                    }}
                  >
                    Business Information
                  </span>
                </div>

                <div
                  onClick={() => scrollToSection(paymentSetupRef)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${
                        paymentSetupStatus === "completed"
                          ? successGreen
                          : paymentSetupStatus === "active_with_issues" ||
                              paymentSetupStatus === "in_progress"
                            ? primaryOrange
                            : paymentSetupStatus === "blocked_with_issues"
                              ? errorRed
                              : netural400
                      }`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        paymentSetupStatus === "completed"
                          ? successGreen
                          : paymentSetupStatus === "active_with_issues" ||
                              paymentSetupStatus === "in_progress"
                            ? primaryOrange
                            : paymentSetupStatus === "blocked_with_issues"
                              ? errorRed
                              : "transparent",
                    }}
                  >
                    {paymentSetupStatus === "completed" && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                    {(paymentSetupStatus === "active_with_issues" ||
                      paymentSetupStatus === "blocked_with_issues") && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        !
                      </span>
                    )}
                    {paymentSetupStatus === "in_progress" && (
                      <span style={{ color: "white", fontSize: "10px" }}>
                        ⏳
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      color:
                        paymentSetupStatus === "completed"
                          ? success700
                          : paymentSetupStatus === "active_with_issues" ||
                              paymentSetupStatus === "in_progress"
                            ? primaryOrange
                            : paymentSetupStatus === "blocked_with_issues"
                              ? error900
                              : netural400,
                      fontWeight:
                        paymentSetupStatus !== "not_started" ? 600 : 500,
                    }}
                  >
                    Payment Setup (Stripe)
                  </span>
                </div>
              </div>
            </NoticeBanner>

            {/* Business Information Section */}
            <div ref={businessInfoRef}>
              <CollapsibleCard
                title="Step 1: Business Information"
                subtitle={
                  verificationStatus === "Approved"
                    ? "Approved - You can update anytime"
                    : verificationStatus === "Rejected"
                      ? "Rejected - Please review and resubmit"
                      : verificationStatus === "Processing" ||
                          verificationStatus === "Reverifying"
                        ? "Processing - Under review"
                        : "Pending - Submit your information"
                }
                headerIcon={
                  verificationStatus === "Approved" ? (
                    <span style={{ fontSize: "28px" }}>✓</span>
                  ) : verificationStatus === "Rejected" ? (
                    <span style={{ fontSize: "28px" }}>✕</span>
                  ) : verificationStatus === "Processing" ||
                    verificationStatus === "Reverifying" ? (
                    <span style={{ fontSize: "28px" }}>⏳</span>
                  ) : (
                    <span style={{ fontSize: "28px" }}>📋</span>
                  )
                }
                headerIconColor={getStatusColors(verificationStatus).icon}
                headerBackgroundColor={
                  getStatusColors(verificationStatus).background
                }
                borderColor={getStatusColors(verificationStatus).border}
                isExpanded={isBusinessInfoExpanded}
                onToggle={() =>
                  setIsBusinessInfoExpanded(!isBusinessInfoExpanded)
                }
                isControlled={true}
              >
                <NoticeBanner
                  title={
                    verificationStatus === "Approved"
                      ? business_approved
                      : verificationStatus === "Pending"
                        ? business_pending
                        : verificationStatus === "Processing" ||
                            verificationStatus === "Reverifying"
                          ? business_processing
                          : verificationStatus === "Rejected"
                            ? business_rejected + rejectionReason
                            : null
                  }
                />
                {loadingBusinessVerification ||
                loadingBusinessVerificationRequest ? (
                  <LoadingSpinner size="medium" overlay={true} />
                ) : (
                  <Form
                    validated={validated}
                    onSubmit={handleSubmit}
                    style={{ marginTop: "2%" }}
                  >
                    <Row className="mb-3">
                      <Col
                        xl={12}
                        lg={12}
                        md={12}
                        style={{ marginBottom: "3%", paddingLeft: 0 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                          className="mt-3"
                        >
                          {/* Tax Rate */}
                          <Col md="5" lg="5">
                            <Form.Group
                              controlId="validationCustom01"
                              style={{ color: netural400 }}
                            >
                              <Form.Label>Tax rate</Form.Label>
                              <Form.Control
                                required
                                type="text"
                                inputMode="numeric"
                                placeholder="Tax rate"
                                name="taxRate"
                                value={reverificationInfo.taxRate}
                                minLength={15}
                                maxLength={50}
                                onChange={handleInputChange}
                                disabled={isFormDisabled}
                                isInvalid={!!errorFields.taxRate}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errorFields.taxRate}
                              </Form.Control.Feedback>
                              <div
                                style={{ textAlign: "left", marginTop: "0.5%" }}
                              >
                                <CustomLabel
                                  style={{ color: netural400, fontSize: 12 }}
                                >
                                  Enter tax percentage, e.g.: 13%
                                </CustomLabel>
                              </div>
                            </Form.Group>
                          </Col>
                        </div>

                        {/* Terms and Conditions */}
                        <Col md="12" lg="12" className="mt-3">
                          <Form.Group
                            controlId="validationCustom01"
                            style={{ color: netural400 }}
                          >
                            <Form.Label>Terms and Conditions *</Form.Label>
                            <Form.Control
                              required
                              as="textarea"
                              rows={3}
                              placeholder="Enter your terms and conditions here.."
                              name="termsAndConditions"
                              value={reverificationInfo.termsAndConditions}
                              minLength={100}
                              maxLength={10000}
                              onChange={handleInputChange}
                              disabled={isFormDisabled}
                              isInvalid={!!errorFields.termsAndConditions}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errorFields.termsAndConditions}
                            </Form.Control.Feedback>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: "0.5%",
                              }}
                            >
                              <CustomLabel
                                style={{
                                  color: netural400,
                                  fontSize: 12,
                                  marginRight: "1.5%",
                                }}
                              >
                                Min. 100 characters
                              </CustomLabel>
                              {reverificationInfo.termsAndConditions.length <
                              10000 ? (
                                <CustomLabel
                                  style={{ color: netural400, fontSize: 12 }}
                                >
                                  {reverificationInfo.termsAndConditions.length}
                                  /10000
                                </CustomLabel>
                              ) : (
                                <CustomLabel
                                  style={{
                                    color: "rgba(242, 38, 19, 0.9)",
                                    fontSize: 12,
                                  }}
                                >
                                  Max length reached -{" "}
                                  {reverificationInfo.termsAndConditions.length}
                                  /10000
                                </CustomLabel>
                              )}
                            </div>
                          </Form.Group>
                        </Col>

                        {/* Privacy Policy */}
                        <Col md="12" lg="12" className="mt-3">
                          <Form.Group
                            controlId="validationCustom01"
                            style={{ color: netural400 }}
                          >
                            <Form.Label>Privacy Policy</Form.Label>
                            <Form.Control
                              required
                              as="textarea"
                              rows={3}
                              placeholder="Enter your privacy policy here.."
                              name="privacyPolicy"
                              value={reverificationInfo.privacyPolicy}
                              minLength={100}
                              maxLength={10000}
                              onChange={handleInputChange}
                              disabled={isFormDisabled}
                              isInvalid={!!errorFields.privacyPolicy}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errorFields.privacyPolicy}
                            </Form.Control.Feedback>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: "0.5%",
                              }}
                            >
                              <CustomLabel
                                style={{
                                  color: netural400,
                                  fontSize: 12,
                                  marginRight: "1.5%",
                                }}
                              >
                                Min. 100 characters
                              </CustomLabel>
                              {reverificationInfo.privacyPolicy.length <
                              10000 ? (
                                <CustomLabel
                                  style={{ color: netural400, fontSize: 12 }}
                                >
                                  {reverificationInfo.privacyPolicy.length}
                                  /10000
                                </CustomLabel>
                              ) : (
                                <CustomLabel
                                  style={{
                                    color: "rgba(242, 38, 19, 0.9)",
                                    fontSize: 12,
                                  }}
                                >
                                  Max length reached -{" "}
                                  {reverificationInfo.privacyPolicy.length}
                                  /10000
                                </CustomLabel>
                              )}
                            </div>
                          </Form.Group>
                        </Col>

                        {/* Location */}
                        <Col md="5" lg="5" className="mt-3">
                          <Form.Group
                            controlId="radioButtonGroup"
                            style={{ color: netural400 }}
                          >
                            <Form.Label>Where are you located?</Form.Label>
                            <div style={{ display: "flex" }}>
                              <Form.Check
                                type="radio"
                                name="region"
                                id="florida-us"
                                label="Florida - US"
                                value="Florida - US"
                                checked={
                                  reverificationInfo.location === "Florida - US"
                                }
                                onChange={handleInputChange}
                                disabled={isFormDisabled}
                              />
                              <Form.Check
                                type="radio"
                                name="region"
                                id="on-canada"
                                label="ON - Canada"
                                value="ON - Canada"
                                checked={
                                  reverificationInfo.location === "ON - Canada"
                                }
                                onChange={handleInputChange}
                                disabled={isFormDisabled}
                                style={{ marginLeft: "5%" }}
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Col>
                      <Col md="5" lg="5" style={{ paddingLeft: 0 }}>
                        <CustomButton
                          title={"Submit"}
                          buttonType={isFormDisabled ? "validation" : "default"}
                          isValid={!isFormDisabled}
                          handleButtonClick={handleSubmit}
                        />
                      </Col>
                    </Row>
                  </Form>
                )}
              </CollapsibleCard>
            </div>

            {/* Payment Setup Section */}
            <div ref={paymentSetupRef}>
              <CollapsibleCard
                title="Step 2: Payment Setup"
                subtitle={paymentSetupStyling.subtitle}
                headerIcon={paymentSetupStyling.headerIcon}
                headerIconColor={paymentSetupStyling.icon}
                headerBackgroundColor={paymentSetupStyling.background}
                borderColor={paymentSetupStyling.border}
                isExpanded={isPaymentSetupExpanded}
                onToggle={() =>
                  setIsPaymentSetupExpanded(!isPaymentSetupExpanded)
                }
                isControlled={true}
              >
                {isStripeStatusLoading ? (
                  <LoadingSpinner size="medium" overlay={false} />
                ) : stripeStatusError ? (
                  <NoticeBanner variant="error" title={stripeStatusError}>
                    <CustomButton
                      title="Retry"
                      buttonType="default"
                      handleButtonClick={handleFetchStripeStatus}
                    />
                  </NoticeBanner>
                ) : (
                  <div>
                    {stripeVerificationStatus?.actionRequired && (
                      <NoticeBanner variant="warning">
                        <strong>Action Required:</strong>{" "}
                        {stripeVerificationStatus.actionRequired}
                      </NoticeBanner>
                    )}

                    {hasStripeErrors && stripeErrorMessages.length > 0 && (
                      <NoticeBanner
                        variant="error"
                        title="Stripe Verification Issues"
                      >
                        <div>
                          {stripeErrorMessages.length === 1 ? (
                            <p>{stripeErrorMessages[0]}</p>
                          ) : (
                            <ul style={{ margin: "0", paddingLeft: "20px" }}>
                              {stripeErrorMessages.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </NoticeBanner>
                    )}

                    {/* Status Indicators */}
                    <div style={{ marginBottom: "20px" }}>
                      <h6 style={{ marginBottom: "15px", fontWeight: 600 }}>
                        Payment Account Status
                      </h6>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}>
                            {stripeVerificationStatus?.detailsSubmitted
                              ? "✓"
                              : "○"}
                          </span>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span
                              style={{
                                color: neturalBlack,
                              }}
                            >
                              Details Submitted
                            </span>
                            <span
                              style={{
                                color: neutral200,
                                fontSize: "12px",
                              }}
                            >
                              After you complete the Stripe form with your
                              business and personal information.
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}>
                            {stripeVerificationStatus?.chargesEnabled
                              ? "✓"
                              : "○"}
                          </span>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span
                              style={{
                                color: neturalBlack,
                              }}
                            >
                              Can Accept Charges
                            </span>
                            <span
                              style={{
                                color: neutral200,
                                fontSize: "12px",
                              }}
                            >
                              Stripe reviews your information (usually within
                              2-3 business days) and approves your account.
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}>
                            {stripeVerificationStatus?.payoutsEnabled
                              ? "✓"
                              : "○"}
                          </span>

                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span
                              style={{
                                color: neturalBlack,
                              }}
                            >
                              Payouts Enabled
                            </span>
                            <span
                              style={{
                                color: neutral200,
                                fontSize: "12px",
                              }}
                            >
                              After you add your bank account in the Stripe
                              dashboard, you can receive payments.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div
                      style={{
                        display: "flex",
                        gap: "15px",
                        flexWrap: "wrap",
                        width: "41%",
                      }}
                    >
                      {/* Fully Completed - No Issues */}
                      {paymentSetupStatus === "completed" && (
                        <>
                          <p
                            style={{
                              color: successGreen,
                              fontWeight: 600,
                              marginBottom: "15px",
                              width: "100%",
                            }}
                          >
                            ✓ Your account is fully verified and ready to accept
                            payments.
                          </p>
                          {stripeDashboardUrl && (
                            <CustomButton
                              title="View Stripe Dashboard"
                              buttonType="default"
                              handleButtonClick={handleOpenStripeDashboard}
                            />
                          )}
                        </>
                      )}

                      {/* Active but has issues - Can accept payments but needs attention */}
                      {paymentSetupStatus === "active_with_issues" && (
                        <>
                          <p
                            style={{
                              color: primaryOrange,
                              fontWeight: 600,
                              marginBottom: "15px",
                              width: "100%",
                            }}
                          >
                            ⚠ Your account can accept payments, but there are
                            issues that need your attention.
                          </p>
                          {stripeDashboardUrl && (
                            <CustomButton
                              title="Fix Issues in Stripe Dashboard"
                              buttonType="default"
                              handleButtonClick={handleOpenStripeDashboard}
                            />
                          )}
                        </>
                      )}

                      {/* Blocked with issues - Cannot accept payments */}
                      {paymentSetupStatus === "blocked_with_issues" && (
                        <>
                          <p
                            style={{
                              color: errorRed,
                              fontWeight: 600,
                              marginBottom: "15px",
                              width: "100%",
                            }}
                          >
                            ✕ Please resolve the verification issues to start
                            accepting payments.
                          </p>
                          {(stripeOnboardingUrl || stripeDashboardUrl) && (
                            <CustomButton
                              title="Resolve Issues in Stripe"
                              buttonType="default"
                              handleButtonClick={
                                stripeDashboardUrl
                                  ? handleOpenStripeDashboard
                                  : handleOpenStripeOnboarding
                              }
                            />
                          )}
                        </>
                      )}

                      {/* In Progress - Details submitted, awaiting review */}
                      {paymentSetupStatus === "in_progress" && (
                        <>
                          <p
                            style={{
                              color: primaryOrange,
                              fontWeight: 600,
                              marginBottom: "15px",
                              width: "100%",
                            }}
                          >
                            ⏳ Stripe is reviewing your account. This usually
                            takes 2-3 business days.
                          </p>
                          {stripeOnboardingUrl && (
                            <CustomButton
                              title="Continue Payment Setup"
                              buttonType="default"
                              handleButtonClick={handleOpenStripeOnboarding}
                            />
                          )}
                          {stripeDashboardUrl && (
                            <CustomButton
                              title="View Stripe Dashboard"
                              buttonType="validation"
                              isValid={true}
                              handleButtonClick={handleOpenStripeDashboard}
                            />
                          )}
                        </>
                      )}

                      {/* Not Started */}
                      {paymentSetupStatus === "not_started" && (
                        <>
                          <p
                            style={{
                              color: netural400,
                              fontWeight: 500,
                              marginBottom: "15px",
                              width: "100%",
                            }}
                          >
                            Connect your Stripe account to start accepting
                            payments.
                          </p>
                          {stripeOnboardingUrl && (
                            <CustomButton
                              title="Start Payment Setup"
                              buttonType="default"
                              handleButtonClick={handleOpenStripeOnboarding}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </CollapsibleCard>
            </div>
          </div>
        )}
      </Fragment>
    </div>
  );
};

export default BusinessInfoForm;
